import { callCoinGeckoAPI } from './api';

/**
 * ATH/Min2Y Service - Service pour récupérer et cacher les données ATH et minimum 2 ans
 *
 * Les données ATH/Min2Y sont permanentes (historiques) et ne nécessitent pas de mise à jour fréquente.
 * Cache permanent dans localStorage.
 */
const CACHE_KEY = 'wealthhub_ath_cache';

export interface ATHData {
  ath: number | null;
  athDate: string | null;
  min2Y: number | null;
  lastUpdated: number;
  fromCache?: boolean; // Indicates if data was loaded from cache
}

export interface ATHCache {
  [coinId: string]: ATHData;
}

export interface ATHFetchResult {
  data: ATHCache;
  stats: {
    cached: number;
    fetched: number;
    failed: number;
  };
}

export const ATHService = {
  /**
   * Get cache from localStorage
   */
  getCache(): ATHCache {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error reading ATH cache:', error);
    }
    return {};
  },

  /**
   * Save cache to localStorage
   */
  saveCache(cache: ATHCache): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error saving ATH cache:', error);
    }
  },

  /**
   * Get ATH and Min 2Y data for a single token
   * Cache permanent (données historiques)
   * @param forceRefresh - Force refresh from API even if cached
   */
  async getATHMinForToken(coinId: string, forceRefresh: boolean = false): Promise<ATHData | null> {
    // Check cache first (unless force refresh)
    const cache = this.getCache();
    if (cache[coinId] && !forceRefresh) {
      // ATH data is permanent, no expiration
      return { ...cache[coinId], fromCache: true };
    }

    try {
      // Fetch from CoinGecko API (uses proxy in production)
      const data = await callCoinGeckoAPI(`/coins/${coinId}`, {
        localization: false,
        tickers: false,
        community_data: false,
        developer_data: false,
        sparkline: false
      });

      if (data.market_data) {
        const athData: ATHData = {
          ath: data.market_data.ath?.usd || null,
          athDate: data.market_data.ath_date?.usd || null,
          min2Y: null, // Will be calculated from price history
          lastUpdated: Date.now()
        };

        // Try to get 2Y minimum from market data
        // Strategy: Try market_chart first for accurate 2Y data, fallback to ATL if unavailable

        // Step 1: Try to fetch from market chart (730 days) for accurate 2Y minimum
        try {
          const chartData = await callCoinGeckoAPI(`/coins/${coinId}/market_chart`, {
            vs_currency: 'usd',
            days: 730,
            interval: 'daily'
          });

          if (chartData.prices && Array.isArray(chartData.prices) && chartData.prices.length > 0) {
            // Find minimum price in 2Y data
            const prices = chartData.prices.map((p: [number, number]) => p[1]);
            athData.min2Y = Math.min(...prices);
            console.log(`✅ Got accurate 2Y min for ${coinId}: ${athData.min2Y}`);
          }
        } catch (error) {
          console.warn(`⚠️ Could not fetch 2Y chart data for ${coinId}:`, error);
        }

        // Note: We no longer fallback to ATL - either we have real 2Y data or we show nothing
        if (athData.min2Y === null) {
          console.log(`ℹ️ No 2Y min data available for ${coinId} (ATL fallback disabled)`);
        }

        // Save to cache (permanent)
        cache[coinId] = athData;
        this.saveCache(cache);

        return { ...athData, fromCache: false };
      }

      return null;
    } catch (error) {
      console.error(`Error fetching ATH/Min2Y for ${coinId}:`, error);
      return null;
    }
  },

  /**
   * Get ATH/Min2Y data for multiple tokens using optimized batch endpoint
   * Uses /coins/markets endpoint which returns ATH/ATL for multiple coins in one request
   * @param coinIds - Array of coin IDs
   * @param forceRefresh - Force refresh from API even if cached
   */
  async getATHMinForTokensBatch(
    coinIds: string[],
    batchSize: number = 250, // CoinGecko markets endpoint supports up to 250 coins
    delay: number = 300,
    forceRefresh: boolean = false
  ): Promise<ATHFetchResult> {
    const results: ATHCache = {};
    const cache = this.getCache();
    const stats = { cached: 0, fetched: 0, failed: 0 };

    // Determine which coins need to be fetched
    const idsToFetch = forceRefresh ? coinIds : coinIds.filter(id => !cache[id]);

    // Return cached data immediately for cached coins (if not forcing refresh)
    if (!forceRefresh) {
      coinIds.forEach(id => {
        if (cache[id]) {
          results[id] = { ...cache[id], fromCache: true };
          stats.cached++;
        }
      });
    }

    if (idsToFetch.length === 0) {
      console.log(`✅ All ${stats.cached} ATH/Min2Y entries loaded from cache`);
      return { data: results, stats };
    }

    console.log(`⏳ Fetching ATH/Min2Y for ${idsToFetch.length} tokens using optimized batch endpoint`);

    // Process in batches using /coins/markets endpoint (more efficient)
    for (let i = 0; i < idsToFetch.length; i += batchSize) {
      const batch = idsToFetch.slice(i, i + batchSize);

      try {
        // Use /coins/markets endpoint which returns ATH/ATL for multiple coins in one request
        // This is MUCH more efficient than individual /coins/{id} calls
        const marketsData = await callCoinGeckoAPI('/coins/markets', {
          vs_currency: 'usd',
          ids: batch.join(','),
          order: 'market_cap_desc',
          sparkline: false,
          price_change_percentage: '24h'
        });

        if (Array.isArray(marketsData)) {
          // Process market data
          for (const coinData of marketsData) {
            const athData: ATHData = {
              ath: coinData.ath || null,
              athDate: coinData.ath_date || null,
              min2Y: null, // Min 2Y requires fetching 730-day chart data - batch endpoint doesn't provide it
              lastUpdated: Date.now(),
              fromCache: false
            };

            results[coinData.id] = athData;

            // Save to cache
            cache[coinData.id] = athData;
            stats.fetched++;
          }

          // Mark failed coins
          const fetchedIds = marketsData.map(c => c.id);
          const failedIds = batch.filter(id => !fetchedIds.includes(id));
          stats.failed += failedIds.length;

          if (failedIds.length > 0) {
            console.warn(`⚠️ Could not fetch data for: ${failedIds.join(', ')}`);
          }
        }
      } catch (error) {
        console.error(`❌ Error fetching batch ATH data:`, error);
        stats.failed += batch.length;
      }

      // Delay between batches to respect rate limits
      if (i + batchSize < idsToFetch.length) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // Save updated cache
    this.saveCache(cache);

    console.log(`✅ ATH/Min2Y: ${stats.cached} cached, ${stats.fetched} fetched, ${stats.failed} failed`);

    return { data: results, stats };
  },

  /**
   * LEGACY: Get ATH/Min2Y data for multiple tokens in batch (old method)
   * This method makes individual API calls per token (less efficient)
   * Kept for fallback purposes
   */
  async getATHMinForTokensBatchLegacy(
    coinIds: string[],
    batchSize: number = 5,
    delay: number = 300,
    forceRefresh: boolean = false
  ): Promise<ATHFetchResult> {
    const results: ATHCache = {};
    const cache = this.getCache();
    const stats = { cached: 0, fetched: 0, failed: 0 };

    // Determine which coins need to be fetched
    const idsToFetch = forceRefresh ? coinIds : coinIds.filter(id => !cache[id]);

    // Return cached data immediately for cached coins (if not forcing refresh)
    if (!forceRefresh) {
      coinIds.forEach(id => {
        if (cache[id]) {
          results[id] = { ...cache[id], fromCache: true };
          stats.cached++;
        }
      });
    }

    if (idsToFetch.length === 0) {
      console.log(`✅ All ${stats.cached} ATH/Min2Y entries loaded from cache`);
      return { data: results, stats };
    }

    console.log(`⏳ Fetching ATH/Min2Y for ${idsToFetch.length} tokens (batch size: ${batchSize})`);

    // Process in batches
    for (let i = 0; i < idsToFetch.length; i += batchSize) {
      const batch = idsToFetch.slice(i, i + batchSize);

      const promises = batch.map(async (coinId) => {
        try {
          const result = await this.getATHMinForToken(coinId, forceRefresh);
          return { coinId, result, success: true };
        } catch (error) {
          console.warn(`Error in batch ATH for ${coinId}:`, error);
          return { coinId, result: null, success: false };
        }
      });

      const batchResults = await Promise.allSettled(promises);

      batchResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          if (result.value.result) {
            results[result.value.coinId] = result.value.result;
            stats.fetched++;
          } else {
            stats.failed++;
          }
        } else {
          stats.failed++;
        }
      });

      // Delay between batches to respect rate limits
      if (i + batchSize < idsToFetch.length) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    console.log(`✅ ATH/Min2Y: ${stats.cached} cached, ${stats.fetched} fetched, ${stats.failed} failed`);

    return { data: results, stats };
  },

  /**
   * Clear cache (useful for testing or forcing refresh)
   */
  clearCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
      console.log('✅ ATH cache cleared');
    } catch (error) {
      console.error('Error clearing ATH cache:', error);
    }
  }
};
